import React, { useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import { Label } from '@rocco/components/label';
import { Badge } from '@rocco/components/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Users, Link2 } from 'lucide-react';
import {
    DOCUMENT_ROLE,
    type DocumentRole,
    type DocumentCollaborator,
    type DocumentCollaboratorSelection,
} from '../../types';
import { getInitials } from '../../utils/get-initials';

/** Special value for "No Access" state */
const NO_ACCESS = 'no-access' as const;

/** Special value for "Owner" state - display only, not a real role */
const OWNER_ROLE = 'Owner' as const;

/**
 * Props for the DocumentCollaboratorPicker component.
 */
interface DocumentCollaboratorPickerProps {
    /** The ID of the BuildHub project (optional for new documents) */
    buildHubId?: string;
    /** The ID of the document (optional for new documents) */
    documentId?: string;
    /** All BuildHub collaborators to choose from */
    buildHubCollaborators: DocumentCollaborator[];
    /** Currently selected collaborators with access */
    selectedCollaborators: DocumentCollaboratorSelection[];
    /** Callback when collaborators change */
    onCollaboratorsChange?: (
        collaborators: DocumentCollaboratorSelection[]
    ) => void;
    /** Whether the picker is disabled */
    disabled?: boolean;
    /** Email of the document owner to filter out */
    ownerEmail?: string;
    /** Default access level for future team members */
    futureTeamMemberRole: DocumentRole | typeof NO_ACCESS;
    /** Callback when future team member default role changes */
    onFutureTeamMemberRoleChange?: (
        role: DocumentRole | typeof NO_ACCESS
    ) => void;
    /** Access level for anyone with the link */
    linkAccessRole: DocumentRole | typeof NO_ACCESS;
    /** Callback when link access role changes */
    onLinkAccessRoleChange?: (role: DocumentRole | typeof NO_ACCESS) => void;
}

/**
 * People picker for restricted mode document access.
 * Shows all BuildHub collaborators with role selection (including "No Access").
 */
export const DocumentCollaboratorPicker: React.FC<
    DocumentCollaboratorPickerProps
> = ({
    buildHubId: _buildHubId,
    documentId: _documentId,
    buildHubCollaborators,
    selectedCollaborators,
    onCollaboratorsChange,
    disabled = false,
    ownerEmail,
    futureTeamMemberRole,
    onFutureTeamMemberRoleChange,
    linkAccessRole,
    onLinkAccessRoleChange,
}) => {
    // Helper to check if an email is the owner (case-insensitive)
    const isOwner = (email: string): boolean => {
        if (!ownerEmail) return false;
        return email.toLowerCase() === ownerEmail.toLowerCase();
    };

    // Convert role to verb form for description text
    const getRoleVerb = (role: DocumentRole | typeof NO_ACCESS): string => {
        switch (role) {
            case DOCUMENT_ROLE.Viewer:
                return 'view';
            case DOCUMENT_ROLE.Commenter:
                return 'vote & comment';
            case DOCUMENT_ROLE.Editor:
                return 'add & edit pins';
            case NO_ACCESS:
                return 'no access';
            default:
                return 'no access';
        }
    };

    // Get description text for future team members
    const getFutureTeamMemberText = (): string => {
        if (futureTeamMemberRole === NO_ACCESS) {
            return 'New members will have no access';
        }
        return `New members can ${getRoleVerb(futureTeamMemberRole)}`;
    };

    // Get description text for link access
    const getLinkAccessText = (): string => {
        if (linkAccessRole === NO_ACCESS) {
            return 'Link sharing is disabled';
        }
        return `Anyone with the link can ${getRoleVerb(linkAccessRole)}`;
    };

    // Sort collaborators to put owner first
    const sortedCollaborators = useMemo(() => {
        if (!ownerEmail) return buildHubCollaborators;

        return [...buildHubCollaborators].sort((a, b) => {
            const aIsOwner = a.email.toLowerCase() === ownerEmail.toLowerCase();
            const bIsOwner = b.email.toLowerCase() === ownerEmail.toLowerCase();

            if (aIsOwner && !bIsOwner) return -1; // a (owner) comes first
            if (!aIsOwner && bIsOwner) return 1; // b (owner) comes first
            return 0; // maintain original order for non-owners
        });
    }, [buildHubCollaborators, ownerEmail]);

    // Create a map of email -> role for quick lookup
    const roleMap = useMemo(() => {
        const map = new Map<
            string,
            DocumentRole | typeof NO_ACCESS | typeof OWNER_ROLE
        >();
        selectedCollaborators.forEach((c) => map.set(c.email, c.role));
        // Always show owner as Owner role
        if (ownerEmail) {
            map.set(ownerEmail, OWNER_ROLE);
        }
        return map;
    }, [selectedCollaborators, ownerEmail]);

    const handleRoleChange = (email: string, newRole: string) => {
        // Prevent changing owner's role
        if (isOwner(email)) {
            return;
        }

        const collaborator = buildHubCollaborators.find(
            (c) => c.email === email
        );
        if (!collaborator) return;

        if (newRole === NO_ACCESS) {
            // Remove from selected list
            const updated = selectedCollaborators.filter(
                (c) => c.email !== email
            );
            onCollaboratorsChange?.(
                updated.map((c) => ({
                    email: c.email,
                    role: c.role,
                    name: collaborator.name,
                }))
            );
        } else {
            // Add or update in selected list
            const existing = selectedCollaborators.find(
                (c) => c.email === email
            );
            if (existing) {
                // Update existing
                const updated = selectedCollaborators.map((c) =>
                    c.email === email
                        ? {
                              email: c.email,
                              role: newRole as DocumentRole,
                              name: collaborator.name,
                          }
                        : {
                              email: c.email,
                              role: c.role,
                              name: collaborator.name,
                          }
                );
                onCollaboratorsChange?.(updated);
            } else {
                // Add new
                onCollaboratorsChange?.([
                    ...selectedCollaborators.map((c) => ({
                        email: c.email,
                        role: c.role,
                        name:
                            buildHubCollaborators.find(
                                (bc) => bc.email === c.email
                            )?.name || c.email,
                    })),
                    {
                        email,
                        role: newRole as DocumentRole,
                        name: collaborator.name,
                    },
                ]);
            }
        }
    };

    return (
        <div className="space-y-6">
            {/* Collaborators List - All BuildHub members (owner first) */}
            <div className="max-h-96 space-y-1.5 overflow-y-auto">
                {sortedCollaborators.map((collaborator) => {
                    const currentRole =
                        roleMap.get(collaborator.email) || NO_ACCESS;
                    const isCollaboratorOwner = isOwner(collaborator.email);

                    return (
                        <div
                            key={collaborator.email}
                            className="flex items-center gap-2.5 rounded-md border px-2.5 py-1"
                        >
                            <Avatar className="h-7 w-7">
                                {collaborator.image && (
                                    <AvatarImage src={collaborator.image} />
                                )}
                                <AvatarFallback className="text-xs">
                                    {collaborator.isPending
                                        ? (
                                              collaborator.email ||
                                              collaborator.name
                                          )
                                              .charAt(0)
                                              .toUpperCase()
                                        : getInitials(collaborator.name)}
                                </AvatarFallback>
                            </Avatar>

                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                    <div className="truncate text-sm font-medium">
                                        {collaborator.name ||
                                            collaborator.email}
                                    </div>
                                    {collaborator.isPending && (
                                        <Badge
                                            color="secondary"
                                            size="compact"
                                            text="small"
                                            className="shrink-0 p-1.5 text-[10px]"
                                        >
                                            Pending
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            <Select
                                value={currentRole}
                                onValueChange={(value) =>
                                    handleRoleChange(collaborator.email, value)
                                }
                                disabled={disabled || isCollaboratorOwner}
                            >
                                <SelectTrigger className="h-8 w-36 text-left text-sm">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="text-left">
                                    {isCollaboratorOwner ? (
                                        <SelectItem value={OWNER_ROLE} disabled>
                                            <span className="leading-normal">
                                                Owner
                                            </span>
                                        </SelectItem>
                                    ) : (
                                        <>
                                            <SelectItem value={NO_ACCESS}>
                                                <span className="leading-normal">
                                                    No Access
                                                </span>
                                            </SelectItem>
                                            <SelectItem
                                                value={DOCUMENT_ROLE.Viewer}
                                            >
                                                <span className="leading-normal">
                                                    Viewer
                                                </span>
                                            </SelectItem>
                                            <SelectItem
                                                value={DOCUMENT_ROLE.Commenter}
                                            >
                                                <span className="leading-normal">
                                                    Commenter
                                                </span>
                                            </SelectItem>
                                            <SelectItem
                                                value={DOCUMENT_ROLE.Editor}
                                            >
                                                <span className="leading-normal">
                                                    Editor
                                                </span>
                                            </SelectItem>
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                    );
                })}

                {sortedCollaborators.length === 0 && (
                    <div className="py-8 text-center text-muted-foreground">
                        No project members found
                    </div>
                )}
            </div>

            {/* General Access Section */}
            <div className="space-y-2">
                <Label className="text-base font-medium">General access</Label>
                {/* Future Team Members Row */}
                <div className="flex items-center gap-2.5 rounded-md border border-dashed px-2.5 py-1">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-200">
                        <Users className="h-4 w-4 text-gray-700" />
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">
                            Future project team
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {getFutureTeamMemberText()}
                        </div>
                    </div>

                    <Select
                        value={futureTeamMemberRole}
                        onValueChange={(value) =>
                            onFutureTeamMemberRoleChange?.(
                                value as DocumentRole | typeof NO_ACCESS
                            )
                        }
                        disabled={disabled}
                    >
                        <SelectTrigger className="h-8 w-36 text-left text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="text-left">
                            <SelectItem value={NO_ACCESS}>
                                <span className="leading-normal">
                                    No Access
                                </span>
                            </SelectItem>
                            <SelectItem value={DOCUMENT_ROLE.Viewer}>
                                <span className="leading-normal">Viewer</span>
                            </SelectItem>
                            <SelectItem value={DOCUMENT_ROLE.Commenter}>
                                <span className="leading-normal">
                                    Commenter
                                </span>
                            </SelectItem>
                            <SelectItem value={DOCUMENT_ROLE.Editor}>
                                <span className="leading-normal">Editor</span>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Anyone with the Link Row */}
                <div className="flex items-center gap-2.5 rounded-md border border-dashed px-2.5 py-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-200">
                        <Link2 className="h-4 w-4 text-gray-700" />
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">
                            Anyone with the link
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {getLinkAccessText()}
                        </div>
                    </div>

                    <Select
                        value={linkAccessRole}
                        onValueChange={(value) =>
                            onLinkAccessRoleChange?.(
                                value as DocumentRole | typeof NO_ACCESS
                            )
                        }
                        disabled={disabled}
                    >
                        <SelectTrigger className="h-8 w-36 text-left text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="text-left">
                            <SelectItem value={NO_ACCESS}>
                                <span className="leading-normal">
                                    No Access
                                </span>
                            </SelectItem>
                            <SelectItem value={DOCUMENT_ROLE.Viewer}>
                                <span className="leading-normal">Viewer</span>
                            </SelectItem>
                            <SelectItem value={DOCUMENT_ROLE.Commenter}>
                                <span className="leading-normal">
                                    Commenter
                                </span>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>
    );
};
